import React from "react";
import axios from "axios";
import Swal from "sweetalert2";
import localforage from "localforage";
import { getCompatibleAccessToken, logoutModernAuth, restoreModernSession } from "./sessionAuth";

axios.defaults.withCredentials = true;

const apiRootHints = [
    'admin',
    'billing',
    'calendar',
    'collaborations',
    'comms',
    'communications',
    'core',
    'counties',
    'ehr',
    'equipment',
    'facilities',
    'filemanager',
    'partner',
    'partners',
    'patient',
    'patients',
    'platform',
    'practitioners',
    'resources',
    'towns',
    'users',
];

let urlSafetyInstalled = false;

export const useAppName = () => {

    return "Uzima Health Care"
}

export const useBaseUrl = () =>{
    const viteConfiguredBaseUrl = (((import.meta as any)?.env?.VITE_API_URL) || "").trim()
    const legacyConfiguredBaseUrl = (process.env.REACT_APP_API_URL || "").trim()

    if (viteConfiguredBaseUrl) {
        return viteConfiguredBaseUrl.replace(/\/+$/, '')
    }

    if (legacyConfiguredBaseUrl) {
        return legacyConfiguredBaseUrl.replace(/\/+$/, '')
    }

    if (typeof window !== 'undefined') {
        const { hostname } = window.location
        if (['localhost', '127.0.0.1'].includes(hostname)) {
            return "http://127.0.0.1:8000"
        }
        return window.location.origin.replace(/\/+$/, '')
    }

    return "http://127.0.0.1:8000"
}

export const buildApiUrl = (baseUrl: string, path: string) => {
    const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path
    return new URL(normalizedPath, normalizedBaseUrl).toString()
}

export const normalizeRequestUrl = (value: any) => {
    if (typeof value !== 'string') {
        return value
    }

    let normalized = value.trim()

    if (!normalized) {
        return normalized
    }

    for (const hint of apiRootHints) {
        const missingSlashPattern = new RegExp(`^(https?:\\/\\/[^/?#]+?)(${hint})([/?#].*|$)`, 'i')
        if (missingSlashPattern.test(normalized)) {
            normalized = normalized.replace(missingSlashPattern, '$1/$2$3')
            break
        }
    }

    if (/^https?:\/\//i.test(normalized)) {
        try {
            const parsed = new URL(normalized)
            parsed.pathname = parsed.pathname.replace(/\/{2,}/g, '/')
            return parsed.toString()
        } catch {
            return normalized
        }
    }

    normalized = normalized.replace(/^\/\/+/, '/')
    normalized = normalized.replace(/\/{2,}/g, '/')

    if (!normalized.startsWith('/')) {
        for (const hint of apiRootHints) {
            if (normalized === hint || normalized.startsWith(`${hint}/`)) {
                return `/${normalized}`
            }
        }
    }

    return normalized
}

export const installGlobalUrlSafety = () => {
    if (urlSafetyInstalled) {
        return
    }

    urlSafetyInstalled = true

    axios.interceptors.request.use((config) => {
        if (typeof config.baseURL === 'string') {
            config.baseURL = normalizeRequestUrl(config.baseURL)
        }

        if (typeof config.url === 'string') {
            config.url = normalizeRequestUrl(config.url)
        }

        return config
    })

    if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
        const globalWindow = window as typeof window & { __uzimaFetchNormalized?: boolean }

        if (!globalWindow.__uzimaFetchNormalized) {
            const originalFetch = window.fetch.bind(window)

            window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
                if (typeof input === 'string') {
                    return originalFetch(normalizeRequestUrl(input), init)
                }

                if (input instanceof URL) {
                    return originalFetch(new URL(normalizeRequestUrl(input.toString())), init)
                }

                if (input instanceof Request) {
                    const normalizedUrl = normalizeRequestUrl(input.url)

                    if (normalizedUrl === input.url) {
                        return originalFetch(input, init)
                    }

                    return originalFetch(new Request(normalizedUrl, input), init)
                }

                return originalFetch(input, init)
            }) as typeof window.fetch

            globalWindow.__uzimaFetchNormalized = true
        }
    }
}

export const getAuthToken = async () => {
    const baseUrl = useBaseUrl()
    return await getCompatibleAccessToken(baseUrl)
}

export const getJsonAuthHeaders = async () => {
    const token = await getAuthToken()
    return {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    }
}

// Utility functions for staff authentication and data retrieval
export const getStaffDetails = async () => {
    try {
        const loginInfo = await localforage.getItem('uzima_login_info');
        if (loginInfo) {
            return loginInfo;
        }
        
        // Fallback to localStorage for backward compatibility
        const localStorageInfo = localStorage.getItem('uzima_login_info');
        if (localStorageInfo) {
            return JSON.parse(localStorageInfo);
        }
        
        return null;
    } catch (error) {
        console.error('Error retrieving staff details:', error);
        return null;
    }
};

export const getStaffName = async () => {
    try {
        const staffDetails = await getStaffDetails();
        if (staffDetails) {
            return `${staffDetails.fname} ${staffDetails.lname}`;
        }
        
        // Fallback to localStorage
        const staffDetailsLocal = localStorage.getItem('staff_details');
        if (staffDetailsLocal) {
            const parsed = JSON.parse(staffDetailsLocal);
            return parsed.full_name || `${parsed.fname} ${parsed.lname}`;
        }
        
        return null;
    } catch (error) {
        console.error('Error retrieving staff name:', error);
        return null;
    }
};

export const isAuthenticated = async () => {
    try {
        const baseUrl = useBaseUrl();
        const session = await restoreModernSession(baseUrl);
        return !!session;
    } catch (error) {
        console.error('Error checking authentication:', error);
        return false;
    }
};

export const logout = async () => {
    try {
        const baseUrl = useBaseUrl();
        await logoutModernAuth(baseUrl);
        window.location.href = '/institution/newlog';
    } catch (error) {
        console.error('Error during logout:', error);
        window.location.href = '/institution/newlog';
    }
};

export const useGetData = async (url: any, tok: any=12345678) => {

    try{

        if(tok==12345678){

            const resp = await axios.get(url, { timeout: 15000, withCredentials: true})

            const {status, message, data} = resp.data

            if(status === "success"){

                return data;

            }else {
                console.warn("GET request returned non-success status:", message);
                return [];

            }

        }else{
            const resp = await axios.get(url, { headers:{ 'token': `${tok}`}, withCredentials: true})

            const {status, message, data} = resp.data

            if(status === "success"){

                return data;

            }else {
                console.warn("GET request returned non-success status:", message);
                return [];

            }

        }


    }catch(error: any){

        let msg = "Unable to establish Server - Client connection, are you offline?"

        if (error.code === 'ECONNABORTED') {
            msg = "Request timed out. Please try again later.";
        }

        console.warn(msg, error);
        return [];

    }

}

export const useHeadersGetData = async (url: any, headers?: Record<string, any>): Promise<any | null> => {

    try{

        const resp = await axios.get(url, { timeout: 15000, headers, withCredentials: true })

        const {status, message, data} = resp.data

        if(status === "success"){

            return data;

        }else{
            console.warn("GET request returned non-success status:", message);
            return []

        }

    }catch(error: any){

        let msg = "Unable to establish Server - Client connection, are you offline?"

        if (error.code === 'ECONNABORTED') {
            msg = "Request closed with zero results. Please try again later.";
        }

        console.warn(msg, error);
        return []

    }

}

export const postData = async (url: any, dat: any) => {

    try{

        const resp = await axios.post(url, dat, {timeout: 15000, withCredentials: true})

        const {status, message, data} = resp.data

        if(status === "success"){

            return data

        }else{

            await Swal.fire({
                icon: "error",
                title: "Error",
                text: "Server says: " + message,
            });

            return []

        }

    }catch(error: any){

        let msg = "Unable to establish Server - Client connection, are you offline?"

        if (error.code === 'ECONNABORTED') {
            msg = "Request timed out. Please try again later.";
        }

        await Swal.fire({
            icon: "warning",
            title: "Connection Error",
            text: msg
        })

        return
    }

}

export const usePostData = async (url: any, dat: any) => {
    return await postData(url, dat);
}

export const useDelete = async (url: any) => {

    try{

        const resp = await axios.delete(url, {timeout: 15000, withCredentials: true})

        const {status, message, data} = resp.data

        if(status === "success"){

            await Swal.fire({
                icon: "success",
                title: "Process Complete",
                text: "The record has been successfully deleted"
            });

            return status

        }else{

            await Swal.fire({
                icon: "error",
                title: "Error",
                text: "Server says: " + message,
            });

        }

    }catch (error: any){

        let msg = "Unable to establish Server - Client connection, are you offline?"

        if (error.code === 'ECONNABORTED') {
            msg = "Request timed out. Please try again later.";
        }

        await Swal.fire({
            icon: "warning",
            title: "Warning",
            text: msg
        });

    }

}


export const useFacilityServices = () => {

    const FetchServices = async () => {
        const url = `${useBaseUrl()}/facilities/service-list`

        return await useGetData(url)
    }

    return FetchServices()

}

export const useCounties = async ()=>{

    const url = `${useBaseUrl()}/facilities/get-counties`

    const formaData = new FormData()

    formaData.append("selector", "9999")

    let resp: any;

    resp = await postData(url, formaData);

    return resp

}

export const useTowns = async (countyId: any) => {

    const url = `${useBaseUrl()}/facilities/get-post-towns`

    const formaData = new FormData()

    formaData.append("county_id", countyId)

    let resp: any;

    resp = await postData(url, formaData);

    return resp

}

export const useBreadcrumbs = (clinicName: any, service:any, item: any, subItem: any) => {

    return (
        <>
            <div className="row">
                <div className="col-12">
                    <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                        <h4 className="mb-sm-0">{clinicName} - {service}</h4>

                        <div className="page-title-right">
                            <ol className="breadcrumb m-0">
                                <li className="breadcrumb-item"><a href="javascript: void(0);">{item}</a></li>
                                <li className="breadcrumb-item active">{subItem}</li>
                            </ol>
                        </div>

                    </div>
                </div>
            </div>
        </>
    )
}

export const useStaffRoles = async ()=>{
    const FetchRoles = async () => {
        const url = `${useBaseUrl()}/facilities/user-roles`

        return await useGetData(url)
    }

    return FetchRoles
}

export const useTokenChecker = async (token: any, staffId: any, facilityId: any) => {

    const url = `${useBaseUrl()}/facilities/check-token`

    const formData = new FormData()

    formData.append("token", token)

    formData.append("staff", staffId)

    formData.append("facility", facilityId)

    let resp: any

    resp = await postData(url, formData)

    return resp

}

export const useFullCategories = async () => {

    const url = `${useBaseUrl()}/facilities/full-categories`

    let resp: any

    resp = await useGetData(url)

    return resp

}

export const useUserNames: any = () => {

    let fName: any = '';
    let lName: any = '';
    let cliniC: any = '';
    let staffId: any = '';
    let avatar: any = '';
    let clinicName: any = '';
    let institution: any = '';


    localforage.getItem("fname").then((fnm: any)=>{
        fName = fnm
    })

    localforage.getItem("lname").then((lnm: any)=>{
        lName = lnm
    })

    localforage.getItem("clinic_id").then((cln: any)=>{
        cliniC = cln
    })

    localforage.getItem("clinic_name").then((clnm: any)=>{
        clinicName = clnm
    })

    localforage.getItem("staff_id").then((stf: any)=>{
        staffId = stf
    })

    localforage.getItem("institution_id").then((inst: any)=>{
        institution = inst
    })

    return ({first_name: fName, last_name: lName, clinic_id: cliniC, clinic_name: clinicName, institution: institution, staff_id: staffId, avatar: avatar})

}

export const useSentenceCase = (word: any) => {

    return word.charAt(0).toUpperCase() + word.slice(1);

}

export const useGreeting = async () => {



    let greeting
    let type

    try {

        const response = await fetch('http://worldtimeapi.org/api/timezone/Africa/Nairobi')

        const data = await response.json();

        type = data.datetime

        const currentHour: any = new Date(data.datetime).toLocaleString('en-US', { timeZone: 'Africa/Nairobi', hour12: false, hour: 'numeric' });

        if (currentHour >= 0 && currentHour < 12) {
            greeting = "Good Morning"
        } else if (currentHour >= 12 && currentHour < 18) {
            greeting = "Good Afternoon"
        } else {
            greeting = "Good Evening"
        }

    } catch (error) {

        console.error('Error fetching time:', error);
    }

    return {greeting, type}

}

export const useDjangoToReactTime = (djangoTime: any) => {

    const djangoTimeStamp = new Date(djangoTime)

    return `${djangoTimeStamp.toLocaleDateString()} at ${djangoTimeStamp.toLocaleTimeString()}`
}
